import { Check, Globe } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  currentLanguage,
  LANGUAGE_NAMES,
  LANGUAGES,
  setLanguage,
  type Language,
} from '../../lib/i18n/index.js';
import { viewTransition } from '../../ui/motion.js';
import './layouts.css';

/**
 * The language control: a globe button that opens a short list of languages
 * (each named in itself, with `lang` set so screen readers pronounce it
 * right). The current language is ticked and the page cross-fades to a new
 * one. A plain disclosure (no React Aria), so the public pages stay small.
 * Escape, a click outside or choosing a language closes it.
 */
export function LanguageMenu() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const listId = useId();
  const current = currentLanguage();

  useEffect(() => {
    if (!open) return undefined;
    const close = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        button.current?.focus();
      }
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  const choose = (language: Language) => {
    setOpen(false);
    if (language !== current) void viewTransition(() => setLanguage(language));
    button.current?.focus();
  };

  return (
    <div className="lang-menu" ref={root}>
      <button
        ref={button}
        type="button"
        className="lang-menu__button"
        aria-label={t('shell.switchLanguage')}
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => {
          setOpen((value) => !value);
        }}
      >
        <Globe aria-hidden="true" />
      </button>
      {open ? (
        <ul className="lang-menu__list" id={listId}>
          {LANGUAGES.map((language) => (
            <li key={language}>
              <button
                type="button"
                className="lang-menu__item"
                lang={language}
                aria-pressed={language === current}
                onClick={() => {
                  choose(language);
                }}
              >
                <span>{LANGUAGE_NAMES[language]}</span>
                {language === current ? <Check aria-hidden="true" /> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
